/**
 * Service API adhésions — version publique pour donoor.org
 *
 * Couche 1 — Transport (ADR-001). Utilise `apiCall` du package
 * `@donoor/data-layer` en mode public (`skipAuth: true` +
 * `skipOrganizationId: true`) puisque ces endpoints sont accessibles
 * sans session.
 */

import { apiCall } from "@donoor/data-layer";
import type {
  CreateMembershipOrderPayload,
  CreateMembershipOrderResult,
  CreateFreeMembershipOrderResult,
  PromoValidationResult,
} from "~/types/membership";

const PUBLIC_OPTS = { skipAuth: true, skipOrganizationId: true } as const;

export interface MembershipFileUploadResult {
  file_id: string;
  file_name: string;
}

export const membershipsApi = {
  async createOrder(
    slug: string,
    payload: CreateMembershipOrderPayload,
    preview?: boolean,
  ): Promise<CreateMembershipOrderResult> {
    const url = `/api/public/memberships/${slug}/create-order${preview ? "?preview=true" : ""}`;
    const res = await apiCall<{ data: CreateMembershipOrderResult }>(url, {
      method: "POST",
      body: payload,
      ...PUBLIC_OPTS,
    });
    return res.data;
  },

  async createFreeOrder(
    slug: string,
    payload: CreateMembershipOrderPayload,
    preview?: boolean,
  ): Promise<CreateFreeMembershipOrderResult> {
    const url = `/api/public/memberships/${slug}/create-free-order${preview ? "?preview=true" : ""}`;
    const res = await apiCall<{ data: CreateFreeMembershipOrderResult }>(url, {
      method: "POST",
      body: payload,
      ...PUBLIC_OPTS,
    });
    return res.data;
  },

  async validatePromo(slug: string, code: string): Promise<PromoValidationResult> {
    const res = await apiCall<{ data: PromoValidationResult }>(
      `/api/public/memberships/${slug}/validate-promo`,
      { method: "POST", body: { code }, ...PUBLIC_OPTS },
    );
    return res.data;
  },

  async confirmOrder(orderId: string, paymentIntentId?: string): Promise<void> {
    await apiCall(
      `/api/public/membership-orders/${orderId}/confirm`,
      {
        method: "POST",
        body: { payment_intent_id: paymentIntentId },
        ...PUBLIC_OPTS,
      },
    );
  },

  /**
   * Annule une commande d'adhésion pending si l'acheteur abandonne (sheet Apple Pay fermée).
   * Idempotent côté backend : ne fait rien si déjà confirmée.
   */
  async cancelOrder(orderId: string): Promise<void> {
    try {
      await apiCall(`/api/public/membership-orders/${orderId}/cancel`, {
        method: "POST",
        ...PUBLIC_OPTS,
      });
    } catch {
      // Non-bloquant : si l'annulation échoue, le cron expirera l'order après 30 min
    }
  },

  /**
   * Upload d'un fichier joint à un custom_field d'adhésion (PDF, JPG, PNG,
   * WebP — max 10 Mo). Retourne `file_id` + `file_name` à stocker dans
   * `participant.customFieldValues` au lieu du File brut. Validé côté
   * backend (mime + extension + taille).
   */
  async uploadFile(file: File): Promise<MembershipFileUploadResult> {
    const formData = new FormData();
    formData.append("file", file);
    const res = await apiCall<{
      success: boolean;
      data?: MembershipFileUploadResult;
      message?: string;
    }>("/api/public/membership-files/upload", {
      method: "POST",
      body: formData,
      ...PUBLIC_OPTS,
    });
    if (!res.success || !res.data) {
      throw new Error(res.message || "Erreur lors de l'upload");
    }
    return res.data;
  },
};
